import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { AdminGate as Gate, ADMIN_KEY as KEY, AdminSwitch } from "@/components/AdminGate";
import { mapThumbUrl } from "@/components/PassageMaps";

const EMPTY = { title: "", places: "", books: "", references: "" };

function UploadForm({ adminKey, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!file || !f.title.trim()) return toast.error("Add a title and choose a map file.");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/maps", fd, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Map added.");
      setF(EMPTY); setFile(null); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-border rounded-3xl p-7 grid gap-3">
      <h2 className="font-serif text-2xl font-600 text-ink">Add a Map</h2>
      <Input data-testid="maps-admin-title" placeholder="Title (e.g. Jesus' Ministry in Galilee)" value={f.title} onChange={set("title")} />
      <Input data-testid="maps-admin-places" placeholder="Places, comma separated (e.g. Galilee, Capernaum, Nazareth)" value={f.places} onChange={set("places")} />
      <Input data-testid="maps-admin-books" placeholder="Bible books, comma separated (e.g. Matthew, Mark, Luke)" value={f.books} onChange={set("books")} />
      <Input data-testid="maps-admin-references" placeholder="Key verses (e.g. Matthew 4:12-25)" value={f.references} onChange={set("references")} />
      <Input data-testid="maps-admin-file" type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <Button data-testid="maps-admin-upload" disabled={busy} onClick={submit} className="h-12 rounded-full bg-gold text-ink hover:bg-ink hover:text-parchment font-700">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Upload Map
      </Button>
    </div>
  );
}

export default function MapsAdmin() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY) || "");
  const [maps, setMaps] = useState([]);
  const [q, setQ] = useState("");
  const [count, setCount] = useState(0);
  const load = () => {
    api.get("/maps", { params: { q } }).then(({ data }) => setMaps(data));
    api.get("/maps/count").then(({ data }) => setCount(data.count));
  };
  useEffect(() => { if (adminKey) load(); }, [adminKey, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try {
      await api.delete(`/maps/${id}`, { headers: { "X-Admin-Key": adminKey } });
      load();
    } catch { toast.error("Could not remove map."); }
  };

  return (
    <div data-testid="maps-admin-page" className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-16">
      <AdminSwitch />
      <h1 className="font-serif text-4xl sm:text-5xl font-700 text-ink mb-8">Bible Map Library</h1>
      {!adminKey ? <Gate onUnlock={setAdminKey} /> : (
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 items-start">
          <UploadForm adminKey={adminKey} onDone={load} />
          <div>
          <p data-testid="maps-admin-count" className="text-sm text-ink/60 mb-2">{count} maps in the library · showing up to 30</p>
          <Input data-testid="maps-admin-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, place, book or chapter (e.g. John 6)" className="mb-4 h-11 rounded-full" />
          <div className="grid sm:grid-cols-2 gap-4" data-testid="maps-admin-list">
            {maps.length === 0 && <p className="text-ink/50 text-sm">No maps uploaded yet.</p>}
            {maps.map((m) => (
              <div key={m.id} className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={`maps-admin-item-${m.id}`}>
                {m.content_type === "application/pdf"
                  ? <div className="aspect-[4/3] flex items-center justify-center bg-ink/5 font-mono text-xs text-ink/50">PDF</div>
                  : <img src={mapThumbUrl(m.id)} alt={m.title} loading="lazy" className="aspect-[4/3] w-full object-cover" />}
                <div className="p-4">
                  <p className="font-serif text-lg font-600 text-ink">{m.title}</p>
                  <p className="text-xs text-ink/55">{[...m.places, ...m.books].join(" · ")}</p>
                  <button data-testid={`maps-admin-delete-${m.id}`} onClick={() => remove(m.id)}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-red-700 hover:underline">
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
